/**
 * Sistema centralizado de permissões do frontend.
 * Todas as verificações de role/permissão devem usar estas funções.
 */
import type { User, Table } from '@/types'

// ═══════════════════════════════════════════════════════════
// Verificações de role do sistema
// ═══════════════════════════════════════════════════════════

export function isSuperadmin(user: User): boolean {
  return user.role === 'superadmin'
}

export function isProfessorOrAdmin(user: User): boolean {
  return user.role === 'professor' || user.role === 'superadmin'
}

// ═══════════════════════════════════════════════════════════
// Verificações de permissão em campanhas
// ═══════════════════════════════════════════════════════════

export function canManageCampaign(user: User, table: Table): boolean {
  if (!user || !table) return false
  if (user.role === 'superadmin') return true
  return String(table.creator_id).trim() === String(user.id).trim()
}

export function isGameMaster(user: User, table: Table): boolean {
  if (!user || !table) return false
  if (user.role === 'superadmin') return true
  return String(table.creator_id).trim() === String(user.id).trim()
}

export function canCreateCampaign(user: User): boolean {
  return user.role === 'professor' || user.role === 'superadmin'
}

// ═══════════════════════════════════════════════════════════
// Sistema de impersonação
// ═══════════════════════════════════════════════════════════

const IMP_TOKEN_KEY = 'imp_token_backup'
const IMP_INFO_KEY = 'imp_info'

export interface ImpersonationInfo {
  tableId: string
  tableName: string
}

export function isImpersonating(): boolean {
  return sessionStorage.getItem(IMP_TOKEN_KEY) !== null
}

export function getImpersonationInfo(): ImpersonationInfo | null {
  const raw = sessionStorage.getItem(IMP_INFO_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as ImpersonationInfo
  } catch {
    return null
  }
}

export function startImpersonation(
  tempToken: string,
  tableId: string,
  tableName: string
): void {
  // Guardar token original
  const originalToken = localStorage.getItem('auth_token')
  if (originalToken) {
    sessionStorage.setItem(IMP_TOKEN_KEY, originalToken)
  }
  // Guardar info da impersonação
  sessionStorage.setItem(IMP_INFO_KEY, JSON.stringify({ tableId, tableName }))
  // Substituir token pelo temporário
  localStorage.setItem('auth_token', tempToken)
}

export function stopImpersonation(): void {
  // Restaurar token original
  const originalToken = sessionStorage.getItem(IMP_TOKEN_KEY)
  if (originalToken) {
    localStorage.setItem('auth_token', originalToken)
  }
  // Limpar dados de impersonação
  sessionStorage.removeItem(IMP_TOKEN_KEY)
  sessionStorage.removeItem(IMP_INFO_KEY)
}
